<template>
    <LoadingComponent :props="loading" />
    <div class="flex items-center gap-2 px-6 mt-3">
        <button @click.prevent="$router.back()" class="lab-line-undo text-xl font-medium text-primary"></button>
        <h2 class="text-xl font-medium text-primary">{{ $t('label.order_details') }}</h2>
    </div>

    <div class="bg-white border-b p-4 sm:p-6 mb-2">
        <div class="text-center mb-10">
            <div v-if="order.status !== enums.orderStatusEnum.CANCELED && order.status !== enums.orderStatusEnum.REJECTED" class="flex flex-col items-center border border-gray-200 rounded-lg p-4 mx-auto mb-4">
                <h3 class="text-xl font-semibold mb-1">{{ $t('message.thank_you') }}</h3>
                <p class="text-sm font-medium">{{ $t('message.order_status_follows') }}</p>
                <h4 class="text-sm font-semibold mb-1">
                    {{ $t('label.order_id') }}: <span class="text-primary">#{{ order.order_serial_no }}</span>
                </h4>
            </div>

            <ul v-if="order.status !== enums.orderStatusEnum.CANCELED && order.status !== enums.orderStatusEnum.REJECTED && order.order_type !== enums.orderTypeEnum.PICK_UP"
                class="w-full flex items-center justify-center pb-12 mt-8">
                <li v-for="track in tracks" class="w-full flex items-center justify-center gap-1 relative">
                    <hr :class="{ '!bg-success': track.step <= order.status }"
                        class="block border-none w-full h-1 rounded-xl bg-gray-200" />
                    <i :class="{ 'lab-fill-save !bg-success text-white': track.step <= order.status }"
                        class="flex-shrink-0 w-7 h-7 !leading-7 text-center rounded-full bg-gray-200 lab-font-size-16" />
                    <hr :class="{ '!bg-success': track.step <= order.status }"
                        class="block border-none w-full h-1 rounded-xl bg-gray-200" />
                    <span
                        class="absolute top-10 left-1/2 -translate-x-1/2 w-14 sm:w-20 text-xs sm:text-sm leading-[18px] text-center">
                        {{ track.title }}</span>
                </li>
            </ul>

            <ul v-if="order.status !== enums.orderStatusEnum.CANCELED && order.status !== enums.orderStatusEnum.REJECTED && order.order_type === enums.orderTypeEnum.PICK_UP"
                class="w-full flex items-center justify-center pb-12 mt-8">
                <li v-for="track in pickupTracks" class="w-full flex items-center justify-center gap-1 relative">
                    <hr :class="{ '!bg-success': track.step <= order.status }"
                        class="block border-none w-full h-1 rounded-xl bg-gray-200" />
                    <i :class="{ 'lab-fill-save !bg-success text-white': track.step <= order.status }"
                        class="flex-shrink-0 w-7 h-7 !leading-7 text-center rounded-full bg-gray-200 lab-font-size-16" />
                    <hr :class="{ '!bg-success': track.step <= order.status }"
                        class="block border-none w-full h-1 rounded-xl bg-gray-200" />
                    <span
                        class="absolute top-10 left-1/2 -translate-x-1/2 w-14 sm:w-20 text-xs sm:text-sm leading-[18px] text-center">
                        {{ track.title }}</span>
                </li>
            </ul>

            <div v-if="order.status === enums.orderStatusEnum.CANCELED"
                class="w-full flex items-center justify-center gap-2 py-3 sm:py-4 px-7 sm:px-10 mx-auto rounded-lg border border-danger text-danger bg-red-100 transition-all duration-500">
                <i class="lab-fill-close-circle sm:text-xl"></i>
                <span class="sm:text-lg font-medium whitespace-nowrap">{{ $t('label.order_cancelled') }}</span>
            </div>
            <div v-if="order.status === enums.orderStatusEnum.REJECTED"
                class="w-full flex items-center justify-center gap-2 py-3 sm:py-4 px-7 sm:px-10 mx-auto rounded-lg border border-danger text-danger bg-red-100 transition-all duration-500">
                <i class="lab-fill-close-circle sm:text-xl"></i>
                <span class="sm:text-lg font-medium whitespace-nowrap">{{ $t('label.order_rejected') }}</span>
            </div>
        </div>

        <div class="row">
            <div class="col-12 md:col-5">
                <div class="mb-6 rounded-lg border ">
                    <h3 class="text-lg px-4 pt-2 pb-2 font-medium text-left">{{ $t('label.order_data') }}</h3>
                    <ul class="p-4 flex flex-col gap-2 border-t border-dashed ">
                        <li class="flex flex-wrap sm:flex-nowrap items-center gap-2">
                            <span class="text-xs font-semibold w-28 flex-shrink-0">{{
                                $t('label.order_id')
                                }}:</span>
                            <span class="text-sm font-semibold">#{{ order.order_serial_no }}</span>
                        </li>
                        <li class="flex flex-wrap sm:flex-nowrap gap-2">
                            <span class="text-xs font-semibold w-28 flex-shrink-0">{{
                                $t('label.order_date')
                                }}:</span>
                            <span class="text-xs font-normal">
                                {{ order.order_date }} {{  order.order_time }}
                            </span>
                        </li>
                        <li class="flex flex-wrap sm:flex-nowrap gap-2">
                            <span class="text-xs font-semibold w-28 flex-shrink-0">{{
                                $t('label.order_type')
                                }}:</span>
                            <span class="text-xs font-normal">
                                {{ enums.orderTypeEnumArray[order.order_type] }}
                            </span>
                        </li>
                        <li class="flex flex-wrap sm:flex-nowrap gap-2" v-if="shippingGuideOrTracking">
                            <span class="text-xs font-semibold w-28 flex-shrink-0">{{
                                $t('label.shipping_tracking_number')
                                }}:</span>
                            <span class="text-xs font-normal">
                                {{ shippingGuideOrTracking }}
                            </span>
                        </li>
                        <li class="flex items-center flex-wrap sm:flex-nowrap gap-2">
                            <span class="text-xs font-semibold w-28 flex-shrink-0">{{
                                $t('label.order_status')
                                }}:</span>
                            <span class="text-xs font-normal px-2 rounded-3xl h-fit" :class="orderStatusClass(order.status)">
                                {{ enums.orderStatusEnumArray[order.status] }}
                            </span>
                        </li>
                        <li class="flex items-center flex-wrap sm:flex-nowrap gap-2">
                            <span class="text-xs font-semibold w-28 flex-shrink-0">{{
                                $t('label.payment_status')
                                }}:</span>
                            <span class="text-xs font-normal px-2 rounded-3xl h-fit"
                                :class="enums.paymentStatusEnum.PAID === order.payment_status ? 'text-success bg-green-100 border border-success' : 'text-amber-600 bg-amber-100 border border-warning'">
                                {{ enums.paymentStatusEnumArray[order.payment_status] }}
                            </span>
                        </li>
                        <li class="flex flex-wrap sm:flex-nowrap gap-2">
                            <span class="text-xs font-semibold w-28 flex-shrink-0">{{
                                $t('label.payment_method')
                                }}:</span>
                            <span class="text-xs font-normal">
                                {{ order.payment_method_name }}
                            </span>
                        </li>
                        <li v-if="order.payment_method_slug === 'transferencia' && order.transferencia_payment_details"
                            class="flex flex-wrap sm:flex-nowrap gap-2">
                            <span class="text-xs font-semibold w-28 flex-shrink-0">{{
                                $t('label.transferencia_payment_details')
                                }}:</span>
                            <span class="text-xs font-normal whitespace-pre-line">
                                {{ order.transferencia_payment_details }}
                            </span>
                        </li>
                    </ul>
                </div>

                <div class="rounded-lg border "
                    v-if="order.order_type === enums.orderTypeEnum.DELIVERY && orderAddress.length > 0"
                    v-for="address in orderAddress" :key="address"
                    :class="address.address_type === enums.addressTypeEnum.SHIPPING ? 'mb-6' : 'mb-6'">
                    <h3 class="text-lg px-4 pt-2 pb-2 font-medium text-left"
                        v-if="address.address_type === enums.addressTypeEnum.SHIPPING">{{ $t('label.shipping_address')
                        }}
                    </h3>
                    <h3 class="text-lg px-4 pt-2 pb-2 font-medium text-left"
                        v-if="address.address_type === enums.addressTypeEnum.BILLING">{{ $t('label.billing_address') }}
                    </h3>
                    <ul class="p-4 flex flex-col gap-2 border-t border-dashed ">
                        <li class="flex flex-wrap sm:flex-nowrap gap-2">
                            <span class="text-xs font-semibold w-20 flex-shrink-0">{{ $t('label.name')
                                }}:</span>
                            <span class="text-xs font-normal">{{ address.full_name }}</span>
                        </li>
                        <li class="flex flex-wrap sm:flex-nowrap gap-2">
                            <span class="text-xs font-semibold w-20 flex-shrink-0">{{ $t('label.phone')
                                }}:</span>
                            <span class="text-xs font-normal">{{ address.country_code }} {{ address.phone
                                }}</span>
                        </li>
                        <li class="flex flex-wrap sm:flex-nowrap gap-2" v-if="address.email">
                            <span class="text-xs font-semibold w-20 flex-shrink-0">{{ $t('label.email')
                                }}:</span>
                            <span class="text-xs font-normal">{{ address.email }}</span>
                        </li>
                        <li class="flex flex-wrap sm:flex-nowrap gap-2">
                            <span class="text-xs font-semibold w-20 flex-shrink-0">
                                {{ $t('label.address') }}:</span>
                            <span class="text-xs font-normal">
                                <span v-if="address.address">{{ address.address }}</span>
                                <span class="block" :class="address.address ? 'mt-0' : ''">
                                    <span v-if="address.city">{{ address.city }},</span>
                                    <span v-if="address.state">{{ address.state }},</span>
                                    <span v-if="address.country">{{ address.country }},</span>
                                    <span v-if="address.zip_code">{{ address.zip_code }}</span>
                                </span>
                            </span>
                        </li>
                    </ul>
                </div>

                <div class="rounded-lg border "
                    v-if="outletAddress && Object.keys(outletAddress).length > 0">
                    <h3 class="text-lg px-4 pt-2 pb-2 font-medium text-left">
                        <span v-if="order.order_type === enums.orderTypeEnum.DELIVERY">
                        {{ $t(outletOwnerInfoLabelKey) }}
                        </span>
                        <span v-else>
                        {{ $t('label.pick_up_address') }}
                        </span>
                    </h3>
                    <ul class="p-4 flex flex-col gap-2 border-t border-dashed ">
                        <li class="flex flex-wrap sm:flex-nowrap gap-2">
                            <span v-if="envOutletValue === 'branches'" class="text-xs font-semibold w-20 flex-shrink-0">
                                {{ $t('label.outlet') }}:
                            </span>
                            <span v-else class="text-xs font-semibold w-20 flex-shrink-0">
                                {{ $t('label.salesman') }}:
                            </span>
                            <span class="text-xs font-normal">{{ outletAddress.name }}</span>
                        </li>
                        <li class="flex flex-wrap sm:flex-nowrap gap-2" v-if="outletAddress.phone">
                            <span class="text-xs font-semibold w-20 flex-shrink-0">{{ $t('label.phone')
                                }}:</span>
                            <span class="text-xs font-normal">{{ outletAddress.country_code }}
                                {{ outletAddress.phone }}</span>
                        </li>
                        <li class="flex flex-wrap sm:flex-nowrap gap-2" v-if="outletAddress.email">
                            <span class="text-xs font-semibold w-20 flex-shrink-0">{{ $t('label.email')
                                }}:</span>
                            <span class="text-xs font-normal">{{ outletAddress.email }}</span>
                        </li>
                        <li class="flex flex-wrap sm:flex-nowrap gap-2">
                            <span class="text-xs font-semibold w-20 flex-shrink-0">
                                {{ $t('label.address') }}:</span>
                            <span class="text-xs font-normal">
                                <span v-if="outletAddress.address">{{ outletAddress.address }}</span>
                                <span class="block" :class="outletAddress.address ? 'mt-0' : ''">
                                    <span v-if="outletAddress.city">{{ outletAddress.city }},</span>
                                    <span v-if="outletAddress.state">{{ outletAddress.state }},</span>
                                    <span v-if="outletAddress.zip_code">{{ outletAddress.zip_code }}</span>
                                </span>
                            </span>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="col-12 md:col-7">
                <div class="w-full">
                    <h3 class="pb-3 font-medium text-left">{{ $t('label.order_summary') }}</h3>
                    <ul class="border-b border-t">
                        <li v-for="orderProduct in orderProducts" :key="orderProduct"
                            class="py-4 flex gap-3 border-b last:border-0 border-dashed ">
                            <img :src="orderProduct.product_image" alt="product"
                                class="w-14 h-14 object-cover rounded-md flex-shrink-0" />
                            <div class="flex flex-col justify-center overflow-hidden">
                                <h4 :class="!orderProduct.variation_names ? '' : ''"
                                    class="text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                                    {{ orderProduct.product_name }}</h4>
                                <p class="text-sm overflow-hidden">{{ orderProduct.variation_names }}</p>
                                <div class="flex flex-wrap items-center justify-between gap-4">
                                    <div class="flex items-center gap-2">
                                        <span class="text-sm font-semibold">
                                            {{ orderProduct.subtotal_currency_price }}
                                        </span>
                                        <span class="text-sm font-medium">
                                            {{ $t('label.quantity') }}: {{ orderProduct.quantity }}
                                        </span>
                                    </div>
                                    <router-link
                                        v-if="setting && setting.site_reviews_enable === enums.activityEnum.ENABLE && order.status === enums.orderStatusEnum.DELIVERED && !orderProduct.product_user_review"
                                        :to="{ name: 'frontend.account.productReview', params: { slug: orderProduct.product_slug } }"
                                        class="text-sm font-semibold py-1 px-3 rounded-full whitespace-nowrap bg-primary/5 text-primary transition-all duration-500 hover:text-white hover:bg-primary">
                                        {{ $t('button.write_review') }}
                                    </router-link>
                                    <router-link
                                        v-if="setting && setting.site_reviews_enable === enums.activityEnum.ENABLE && order.status === enums.orderStatusEnum.DELIVERED && orderProduct.product_user_review"
                                        :to="{ name: 'frontend.account.productReview.edit', params: { slug: orderProduct.product_slug, id: orderProduct.product_user_review_id } }"
                                        class="text-sm font-semibold py-1 px-3 rounded-full whitespace-nowrap bg-primary/5 text-primary transition-all duration-500 hover:text-white hover:bg-primary">
                                        {{ $t('button.edit_review') }}
                                    </router-link>
                                </div>
                            </div>
                        </li>
                    </ul>
                    <ul class="flex flex-col gap-1 py-4 border-b border-dashed">
                        <li class="flex items-center justify-between">
                            <span class="text-sm">{{ $t('label.subtotal') }}</span>
                            <span class="text-sm font-medium">{{ order.subtotal_currency_price }}</span>
                        </li>
                        <li class="flex items-center justify-between">
                            <span class="text-sm">{{ $t('label.tax_fee') }}</span>
                            <span class="text-sm font-medium">{{ order.tax_currency_price }}</span>
                        </li>
                        <li class="flex items-center justify-between">
                            <span class="text-sm">{{ $t('label.shipping_charge') }}</span>
                            <span class="text-sm font-medium">{{ order.shipping_charge_currency_price }}</span>
                        </li>
                        <li class="flex items-center justify-between" v-if="order.shipping_quote && order.shipping_quote.provider">
                            <span class="text-sm">{{ $t('label.shipping_provider') }}</span>
                            <span class="text-sm font-medium">{{ shippingProviderLabel(order.shipping_quote.provider) }}</span>
                        </li>
                        <li class="flex items-center justify-between" v-if="order.shipping_quote && order.shipping_quote.service_level">
                            <span class="text-sm">{{ $t('label.shipping_service') }}</span>
                            <span class="text-sm font-medium">{{ order.shipping_quote.service_level }}</span>
                        </li>
                        <li class="flex items-center justify-between" v-if="order.shipping_quote && order.shipping_quote.days">
                            <span class="text-sm">{{ $t('label.shipping_days') }}</span>
                            <span class="text-sm font-medium">{{ order.shipping_quote.days }}</span>
                        </li>
                        <li class="flex items-center justify-between" v-if="order.shipping_shipment && order.shipping_shipment.tracking_number">
                            <span class="text-sm">{{ $t('label.shipping_tracking_number') }}</span>
                            <span class="text-sm font-medium">{{ order.shipping_shipment.tracking_number }}</span>
                        </li>
                        <li class="flex items-center justify-between" v-if="order.shipping_shipment && order.shipping_shipment.label_url">
                            <span class="text-sm">{{ $t('label.shipping_label') }}</span>
                            <a :href="order.shipping_shipment.label_url" target="_blank" class="text-sm font-medium text-primary underline">{{ $t('label.view') }}</a>
                        </li>
                        <li class="flex items-center justify-between">
                            <span class="text-sm">{{ $t('label.discount') }}</span>
                            <span class="text-sm font-medium">{{ order.discount_currency_price }}</span>
                        </li>
                    </ul>
                    <div class="flex items-center justify-between py-3">
                        <span class="text-sm font-medium">{{ $t('label.total') }}</span>
                        <span class="text-sm font-medium">{{ order.total_currency_price }}</span>
                    </div>
                </div>

                <div class="rw-full mt-6" v-if="deliveries.length > 0">
                    <div class="pb-3">
                        <h3 class="font-medium text-left">{{ $t('label.shipping_deliveries') }}</h3>
                    </div>
                    <ul class="py-4 border-t flex flex-col gap-3">
                        <li v-for="delivery in deliveries" :key="delivery.id" class="rounded-lg border p-3">
                            <div class="flex items-center justify-between gap-3 border-b pb-2 mb-3">
                                <span class="text-sm font-semibold mt-0.5">{{ deliveryTypeLabel(delivery.delivery_type) }}</span>
                                <span class="text-xs px-2 py-1 rounded-md " :class="order.status === enums.orderStatusEnum.CANCELED || order.status === enums.orderStatusEnum.REJECTED ? 'bg-red-100 text-danger' : 'bg-blue-100 text-blue-500'">
                                    {{ shippingDeliveryStatusLabel(delivery.status) }}
                                </span>
                            </div>
                            <p class="text-xs mb-1" v-if="delivery.provider">
                                <span class="font-medium">{{ $t('label.shipping_provider_alt') }}</span>: {{ shippingProviderLabel(delivery.provider) }}
                            </p>
                            <p class="text-xs mb-1" v-if="delivery.tracking_number">
                                <span class="font-medium">{{ $t('label.shipping_tracking_number') }}</span>: {{ delivery.tracking_number }}
                            </p>
                            <p class="text-xs mb-1" v-if="delivery.payload && delivery.payload.label === 'envio_sin_costo'">
                                <span class="font-medium">{{ $t('label.shipping_charge') }}</span>: {{ $t('label.free_shipping') }}
                            </p>
                            <p v-else class="text-xs font-semibold">
                                <span class="font-medium">{{ $t('label.shipping_charge') }}</span>: {{ delivery.shipping_currency_charge }}
                            </p>
                        </li>
                    </ul>
                </div>

                <div class="w-full mt-6" v-if="shippingEvents.length > 0">
                    <div class="pb-3 flex items-center justify-between gap-3">
                        <h3 class="font-medium text-left">{{ $t('label.shipping_follow_up') }}</h3>
                        <button type="button"
                            class="text-xs font-semibold text-primary"
                            @click="toggleShippingEventsSort">
                            {{ shippingEventsSortOrder === 'asc' ? $t('label.shipping_events_newest_first') : $t('label.shipping_events_oldest_first') }}
                        </button>
                    </div>
                    <ul class="py-3 border-t">
                        <li v-for="(event, index) in shippingEvents"
                            :key="event.id"
                            class="shipping-event-timeline-item"
                            :class="index === shippingEvents.length - 1 ? 'shipping-event-timeline-item-last' : ''">
                            <div class="flex items-center justify-start gap-2">
                                <i class="lab lab-line-calendar lab-font-size-16"></i>
                                <span class="text-xs text-heading">{{ event.created_at }}</span>
                            </div>
                            <div class="flex items-center justify-between gap-3">
                                <div>
                                    <p class="text-sm font-medium leading-4 mt-1">
                                        {{ shippingEventMessageLabel(event) }}
                                    </p>
                                    <p class="text-xs" v-if="event.payload.tracking_number">
                                        {{ $t('label.shipping_tracking_number') }}: {{ event.payload.tracking_number }}
                                    </p>
                                    <p class="text-xs" v-if="event.payload.delivery_status">
                                        {{ $t('label.delivery_status') }}: {{ shippingDeliveryStatusLabel(event.payload.delivery_status) }}
                                    </p>
                                    <!-- <p class="text-xs" v-if="event.event">
                                        {{ $t('label.event_type') }}: {{ shippingEventNameLabel(event.event) }}
                                    </p> -->
                                    <!-- <p class="text-xs">
                                        {{ $t('label.source') }}: {{ shippingEventSourceLabel(event.source) }}
                                    </p> -->
                                </div>
                                <span class="px-3 rounded-3xl text-[11px] font-medium nowrap" :class="shippingEventStatusBadgeClass(event.status)">
                                    {{ shippingEventStatusLabel(event.status) }}
                                </span>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>

    <div v-if="order.status !== enums.orderStatusEnum.CANCELED" class="flex flex-wrap justify-start items-center gap-3 mobile:mb-20 px-4 py-6 sm:px-6">
        <OrderReceiptComponent :order="order" :orderProducts="orderProducts" :orderUser="orderUser"
            :orderAddress="orderAddress[0]" />
        <button v-if="order.status === enums.orderStatusEnum.PENDING"
            @click="changeStatus(enums.orderStatusEnum.CANCELED)" type="button"
            class="px-6 py-2 rounded-full whitespace-nowrap text-center font-medium border border-danger text-danger bg-white">
            {{ $t('button.cancel_order') }}
        </button>
        <router-link v-if="order.status === enums.orderStatusEnum.DELIVERED && order.return_and_refund"
            :to="{ name: 'frontend.account.returnOrder.request', params: { id: order.id } }"
            class="px-5 py-1 rounded-full whitespace-nowrap text-center font-medium border border-danger text-danger bg-white">
            {{ $t('button.return_request') }}</router-link>
    </div>

    <div id="payment-modal"
        :class="Object.keys(route.query).length > 0 && route.query.status === 'success' && cart.length > 0 && modal ? 'modal-active' : ''"
        class=" fixed inset-0 z-50 p-3 w-screen h-dvh overflow-y-auto bg-primary/50 transition-all duration-300 opacity-0 flex justify-center items-center invisible">
        <div class="w-full rounded-xl shadow-sm mx-auto bg-white transition-all duration-300 max-w-[360px]">
            <div class="p-8 relative">
                <button @click.prevent="reset" type="button"
                    class="lab-line-cross text-3xl text-[#E93C3C] absolute top-3 right-3"></button>
                <h3 class="font-medium text-center mb-5">{{ $t('message.thank_you_for_your_order') }}</h3>
                <img :src="setting.image_confirm" alt="confirm-image" class="w-[120px] mx-auto mt-2" />
                <h4 class="font-medium text-sm text-center mb-5">{{ $t('message.your_order_is_successfully_placed') }}</h4>
                <button type="button" @click.prevent="reset" class="field-button font-medium tracking-wide normal-case">{{
                    $t('button.see_your_order_details') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import LoadingComponent from "../../components/LoadingComponent";
import orderStatusEnum from "../../../../enums/modules/orderStatusEnum";
import paymentStatusEnum from "../../../../enums/modules/paymentStatusEnum";
import addressTypeEnum from "../../../../enums/modules/addressTypeEnum";
import appService from "../../../../services/appService";
import alertService from "../../../../services/alertService";
import targetService from "../../../../services/targetService";
import { useRoute } from 'vue-router'
import OrderReceiptComponent from "./OrderReceiptComponent";
import orderTypeEnum from "../../../../enums/modules/orderTypeEnum";
import activityEnum from "../../../../enums/modules/activityEnum";
import ENV from "../../../../config/env";

export default {
    name: "OrderDetailsComponent",
    components: { LoadingComponent, OrderReceiptComponent },
    setup() {
        const route = useRoute();
        return { route: route };
    },
    data() {
        return {
            loading: {
                isActive: false,
            },
            tracks: [
                { step: 1, title: this.$t('label.order_pending') },
                { step: 5, title: this.$t('label.order_confirmed') },
                { step: 7, title: this.$t('label.order_on_the_way') },
                { step: 10, title: this.$t('label.order_delivered') },
            ],
            pickupTracks: [
                { step: 1, title: this.$t('label.order_pending') },
                { step: 5, title: this.$t('label.order_confirmed') },
                { step: 10, title: this.$t('label.order_delivered') },
            ],
            enums: {
                orderStatusEnum: orderStatusEnum,
                paymentStatusEnum: paymentStatusEnum,
                addressTypeEnum: addressTypeEnum,
                orderTypeEnum: orderTypeEnum,
                activityEnum: activityEnum,
                orderStatusEnumArray: {
                    [orderStatusEnum.PENDING]: this.$t("label.pending"),
                    [orderStatusEnum.CONFIRMED]: this.$t("label.confirmed"),
                    [orderStatusEnum.ON_THE_WAY]: this.$t("label.on_the_way"),
                    [orderStatusEnum.DELIVERED]: this.$t("label.delivered"),
                    [orderStatusEnum.CANCELED]: this.$t("label.canceled"),
                    [orderStatusEnum.REJECTED]: this.$t("label.rejected"),
                },
                paymentStatusEnumArray: {
                    [paymentStatusEnum.PAID]: this.$t("label.paid"),
                    [paymentStatusEnum.UNPAID]: this.$t("label.unpaid")
                },
                orderTypeEnumArray: {
                    [orderTypeEnum.DELIVERY]: this.$t("label.delivery"),
                    [orderTypeEnum.PICK_UP]: this.$t("label.pick_up")
                }
            },
            shippingEventsSortOrder: 'desc',
            modal: false

        };
    },
    computed: {
        setting: function () {
            return this.$store.getters['frontendSetting/lists'];
        },
        order: function () {
            return this.$store.getters['frontendOrder/show'];
        },
        orderProducts: function () {
            return this.$store.getters['frontendOrder/orderProducts'];
        },
        orderUser: function () {
            return this.$store.getters['frontendOrder/orderUser'];
        },
        orderAddress: function () {
            return this.$store.getters['frontendOrder/orderAddress'];
        },
        outletAddress: function () {
            return this.$store.getters['frontendOrder/outletAddress'];
        },
        deliveries: function () {
            return Array.isArray(this.order?.deliveries) ? this.order.deliveries : [];
        },
        shippingGuideOrTracking: function () {
            if (!this.order || !this.order.shipping_shipment) {
                const firstDeliveryWithTracking = this.deliveries.find((delivery) => !!delivery.tracking_number);
                return firstDeliveryWithTracking ? firstDeliveryWithTracking.tracking_number : null;
            }

            return this.order.shipping_shipment.tracking_number || this.order.shipping_shipment.shipment_id || null;
        },
        shippingEvents: function () {
            if (!this.order || !Array.isArray(this.order.shipping_events)) {
                return [];
            }

            return [...this.order.shipping_events].sort((first, second) => {
                const firstId = Number(first?.id ?? 0);
                const secondId = Number(second?.id ?? 0);

                if (this.shippingEventsSortOrder === 'asc') {
                    return firstId - secondId;
                }

                return secondId - firstId;
            });
        },
        cart: function () {
            return this.$store.getters['frontendCart/lists'];
        },
        paymentMethod: function () {
            return this.$store.getters['frontendCart/paymentMethod'];
        },
        outletOwnerInfoLabelKey: function () {
            return ENV.STORE_OUTLETS_USE === 'branches' ? 'label.outlet_info' : 'label.seller_info';
        },
        envOutletValue: function () {
            return ENV.STORE_OUTLETS_USE;
        }
    },
    mounted() {
        if (this.$route.params.id) {
            this.loading.isActive = true;
            this.$store.dispatch("frontendOrder/show", this.$route.params.id).then(res => {
                if (this.$route.query.status === "success" && this.setting.whatsapp_status === activityEnum.ENABLE && this.setting.whatsapp_number) {
                    this.modal = false;
                    this.handleClick(res.data.data, res.data.data.outlet_address);
                } else {
                    this.modal = true;
                }
                this.loading.isActive = false;
            }).catch((error) => {
                this.loading.isActive = false;
            });
        }
    },
    methods: {
        showTarget: function (id, cClass) {
            targetService.showTarget(id, cClass);
        },
        reset: function () {
            if (this.cart.length > 0 && Object.keys(this.paymentMethod).length > 0 && this.paymentMethod.slug === 'credit') {
                this.$store.dispatch("profile").then().catch();
            }
            this.$store.dispatch("frontendCart/resetCart").then().catch();
        },
        orderStatusClass: function (status) {
            return appService.orderStatusClass(status);
        },
        shippingProviderLabel: function (value) {
            return this.resolveTranslatedLabel('shipping_provider', value);
        },
        deliveryTypeLabel: function (value) {
            const normalized = `${value ?? ''}`.trim().toLowerCase();
            if (normalized === 'broker_provider') {
                return this.$t('label.broker_provider_shipping');
            }

            if (normalized === 'standard_provider_flat_rate') {
                return this.$t('label.flat_rate_shipping');
            }

            if (normalized === 'standard_provider_free') {
                return this.$t('label.free_shipping');
            }

            if (normalized === 'standard_provider') {
                return this.$t('label.standard_provider_shipping');
            }

            return this.formatShippingEventFallback(value);
        },
        shippingDeliveryStatusLabel: function (value) {
            return this.resolveTranslatedLabel('shipping_delivery_status', value);
        },
        shippingEventSourceLabel: function (value) {
            return this.resolveTranslatedLabel('shipping_event_source', value);
        },
        shippingEventNameLabel: function (value) {
            return this.resolveTranslatedLabel('shipping_event_name', value);
        },
        shippingEventStatusLabel: function (value) {
            return this.resolveTranslatedLabel('shipping_event_status', value);
        },
        shippingEventStatusBadgeClass: function (value) {
            const normalized = `${value ?? ''}`.trim().toLowerCase();
            if (normalized === 'success') {
                return 'text-[#2AC769] bg-[#E2FFEE]';
            }

            if (normalized === 'failed') {
                return 'text-[#FB4E4E] bg-[#FFE8E8]';
            }

            return 'text-blue-500 bg-blue-100';
        },
        shippingEventMessageLabel: function (event) {
            const key = `shipping_event_message_${event?.event ?? ''}`;
            if (this.$te(`label.${key}`)) {
                return this.$t(`label.${key}`);
            }

            return event?.message ? event.message : '-';
        },
        resolveTranslatedLabel: function (prefix, value) {
            const normalized = `${value ?? ''}`.trim().toLowerCase();
            const key = `${prefix}_${normalized}`;
            if (normalized && this.$te(`label.${key}`)) {
                return this.$t(`label.${key}`);
            }

            return this.formatShippingEventFallback(value);
        },
        formatShippingEventFallback: function (value) {
            if (!value) {
                return '-';
            }

            return `${value}`
                .replace(/_/g, ' ')
                .replace(/\b\w/g, letter => letter.toUpperCase());
        },
        toggleShippingEventsSort: function () {
            this.shippingEventsSortOrder = this.shippingEventsSortOrder === 'desc' ? 'asc' : 'desc';
        },
        changeStatus: function (status) {
            appService.cancelOrder().then((res) => {
                try {
                    this.loading.isActive = true;
                    this.$store.dispatch("frontendOrder/changeStatus", {
                        id: this.$route.params.id,
                        status: status,
                    }).then((res) => {
                        this.loading.isActive = false;
                        alertService.successFlip(
                            1,
                            this.$t("label.status")
                        );
                    }).catch((err) => {
                        this.loading.isActive = false;
                        alertService.error(err.response.data.message);
                    });
                } catch (err) {
                    this.loading.isActive = false;
                    alertService.error(err.response.data.message);
                }
            }).catch((err) => {
                this.loading.isActive = false;
            });
        },
        handleClick(order, outletAddress) {
            let text = `${this.$t('message.new_order_at')}
**${this.setting.company_name}**

${this.$t('label.order_id')}: #${order.order_serial_no}
${this.$t("label.order_type")}: ${this.enums.orderTypeEnumArray[order.order_type]}
${this.$t('label.order_date')}: ${order.order_date}${order.order_time}
${this.$t('label.order_status')}: ${this.enums.orderStatusEnumArray[order.status]}
${this.$t("label.payment_status")}: ${this.enums.paymentStatusEnumArray[order.payment_status]}
${this.$t("label.payment_method")}: ${order.payment_method_name}

`;

            text += `--------------------------

${this.$t('label.order_details')}:

`;
            for (let i = 0; i < order.order_products.length; i++) {
                const item = order.order_products[i];

                text += `${i + 1})  ${item.product_name}
`;

                text += `--------------------------
${this.$t('label.variation')}: ${item.variation_names}
${this.$t('label.price')}: ${item.currency_price}
${this.$t('label.quantity')}: ${item.quantity}
${this.$t('label.discount')}: ${item.discount_currency_price}
--------------------------
${this.$t('label.amount')}: ${item.subtotal_currency_price}

`;

            }


            text += `${this.$t('label.subtotal')}: ${order.subtotal_currency_price}
${this.$t('label.tax_fee')}: ${order.tax_currency_price}
${this.$t('label.shipping_charge')}: ${message.pending}
${this.$t('label.discount')}: ${order.discount_currency_price}
--------------------------
${this.$t('label.total_amount')}: ${order.total_currency_price}

`;
            if (order.order_type === this.enums.orderTypeEnum.DELIVERY) {

                for (let i = 0; i < order.order_address.length; i++) {
                    let address = order.order_address[i];
                    let address_type = '';
                    if (address.address_type === this.enums.addressTypeEnum.SHIPPING) {
                        address_type = this.$t('label.shipping_address');
                        text += `${address_type}
--------------------------
${this.$t('label.name')}: ${address.full_name}
${this.$t('label.phone')}: ${address.country_code} ${address.phone}
${this.$t('label.email')}: ${address.email}
${this.$t('label.address')}: ${address.address}, ${address.city},  ${address.zip_code}. ${address.state}, ${address.country}.

`;
                    }
//                     else {
//                         address_type = this.$t('label.billing_address');
//                         text += `${address_type}
// --------------------------
// ${this.$t('label.name')}: ${address.full_name}
// ${this.$t('label.phone')}: ${address.country_code}${address.phone}
// ${this.$t('label.email')}: ${address.email}
// ${this.$t('label.address')}: ${address.address}, ${address.city},  ${address.zip_code}. ${address.state}, ${address.country}.

// `;
//                     }
                }
            }

            if (order.order_type === this.enums.orderTypeEnum.PICK_UP) {
                text += `${this.$t('label.pick_up_address')}
--------------------------
${this.$t('label.name')}: ${outletAddress.name}
${this.$t('label.phone')}: ${outletAddress.country_code} ${outletAddress.phone}
${this.$t('label.email')}: ${outletAddress.email}
${this.$t('label.address')}: ${outletAddress.address}, ${outletAddress.city}, ${outletAddress.zip_code}. ${outletAddress.state}, ${outletAddress.country}.

`;
            } else {
                text += `${this.$t(this.outletOwnerInfoLabelKey)}
--------------------------
${this.$t('label.name')}: ${outletAddress.name}
${this.$t('label.phone')}: ${outletAddress.country_code} ${outletAddress.phone}
${this.$t('label.email')}: ${outletAddress.email}
`;
            }


            text += `${this.$t('label.NOTE')}: ${this.$t('message.NOTE')}
`;

            text = encodeURIComponent(text);

            this.reset();

            if (ENV.DEMO === "true" || ENV.DEMO === true || ENV.DEMO === "1" || ENV.DEMO === 1) {
                window.open("https://api.whatsapp.com/send?phone=" + this.setting.whatsapp_calling_code + '' + this.setting.whatsapp_number + "&text=" + text, "_blank");
            } else {

                let whatsapp_num = outletAddress.phone ?? this.setting.whatsapp_number;
                window.location = "https://api.whatsapp.com/send?phone=" + this.setting.whatsapp_calling_code + '' + whatsapp_num + "&text=" + text;
            }
        }
    },
}
</script>